import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { money, fmtDate } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { StatusBadge, Spinner } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, CheckCircle, DollarSign, FileText, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const Input = (p) => <input {...p} className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-emerald-500 w-full" />;
const Sel = ({ children, ...p }) => <select {...p} className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white w-full">{children}</select>;
const Label = ({ children }) => <label className="text-sm font-medium text-slate-700 block mb-1">{children}</label>;

export default function SettlementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { store } = useStore();
  const [s, setS] = useState(null);
  const [payLine, setPayLine] = useState(null); // null | 'all' | line
  const [pay, setPay] = useState({ payment_date: new Date().toISOString().slice(0, 10), payment_method: "Check", reference: "", notes: "" });
  const [adjLine, setAdjLine] = useState(null);
  const [adj, setAdj] = useState({ amount: "", reason: "" });
  const cur = store?.currency || "USD";

  const load = () => api.get(`/settlements/${id}`).then((r) => setS(r.data));
  useEffect(() => { load(); }, [id]);

  const approve = async () => { await api.post(`/settlements/${id}/approve`); toast.success("Settlement approved"); load(); };

  const doPay = async () => {
    const body = { ...pay, consignor_id: payLine === "all" ? null : payLine.consignor_id };
    await api.post(`/settlements/${id}/pay`, body);
    toast.success("Marked paid"); setPayLine(null); load();
  };

  const doAdjust = async () => {
    if (!adj.reason.trim()) return toast.error("Reason is required");
    try {
      await api.post(`/settlements/lines/${adjLine.id}/adjust`, { amount: Number(adj.amount || 0), reason: adj.reason });
      toast.success("Adjustment saved"); setAdjLine(null); setAdj({ amount: "", reason: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!s) return <Spinner />;
  const t = s.totals;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/settlements")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-settlements">
        <ArrowLeft className="w-4 h-4" /> Back to settlements
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{s.label}</h1>
          <p className="text-slate-500 text-sm">{fmtDate(s.start_date)} – {fmtDate(s.end_date)} · {s.consignor_count} consignors</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={s.status} />
          {s.status === "draft" && (
            <button data-testid="approve-btn" onClick={approve} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg text-sm">
              <CheckCircle className="w-4 h-4" /> Approve
            </button>
          )}
          {s.status !== "paid" && s.status !== "draft" && (
            <button data-testid="pay-all-btn" onClick={() => setPayLine("all")} className="flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg text-sm">
              <DollarSign className="w-4 h-4" /> Mark All Paid
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {[["Gross", t.gross_sales, ""], ["Discounts", t.discounts, "text-rose-600"], ["Returns", t.returns, "text-rose-600"],
          ["Net Sales", t.net_sales, ""], ["Consignor", t.consignor_share, "text-amber-700"], ["Store", t.store_share, "text-emerald-700"],
          ["Owed", t.amount_owed, "text-amber-700"]].map(([l, v, c]) => (
          <div key={l} className="bg-white rounded-xl border border-slate-200 p-3">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">{l}</p>
            <p className={`font-mono-fin font-bold mt-1 ${c || "text-slate-900"}`}>{money(v, cur)}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Consignor</th><th className="px-4 py-3 text-center">Items</th><th className="px-4 py-3 text-right">Gross</th>
              <th className="px-4 py-3 text-right">Disc.</th><th className="px-4 py-3 text-right">Returns</th><th className="px-4 py-3 text-right">Net</th>
              <th className="px-4 py-3 text-center">%</th><th className="px-4 py-3 text-right">Adjust</th><th className="px-4 py-3 text-right">Owed</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {s.lines.map((l, i) => (
              <tr key={l.id} data-testid={`settlement-line-${i}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-900">{l.consignor_name}
                  {l.has_adjustment && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 inline ml-1" title={l.adjustment_reason} />}</td>
                <td className="px-4 py-3 text-center">{l.items_sold}</td>
                <td className="px-4 py-3 text-right font-mono-fin">{money(l.gross_sales, cur)}</td>
                <td className="px-4 py-3 text-right font-mono-fin text-rose-600">{l.discounts ? `-${money(l.discounts, cur)}` : "—"}</td>
                <td className="px-4 py-3 text-right font-mono-fin text-rose-600">{l.returns ? money(l.returns, cur) : "—"}</td>
                <td className="px-4 py-3 text-right font-mono-fin">{money(l.net_sales, cur)}</td>
                <td className="px-4 py-3 text-center">{l.payout_pct}%</td>
                <td className="px-4 py-3 text-right font-mono-fin">{l.adjustment ? money(l.adjustment, cur) : "—"}</td>
                <td className="px-4 py-3 text-right font-mono-fin font-bold text-amber-700">{money(l.amount_owed, cur)}</td>
                <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`statement-btn-${i}`} onClick={() => navigate(`/statement/${l.id}`)} className="text-slate-500 hover:text-emerald-700 mr-2" title="Statement"><FileText className="w-4 h-4 inline" /></button>
                  {s.status !== "paid" && l.status !== "paid" && (
                    <>
                      <button data-testid={`adjust-btn-${i}`} onClick={() => { setAdjLine(l); setAdj({ amount: String(l.adjustment || ""), reason: l.adjustment_reason || "" }); }} className="text-slate-500 hover:text-blue-700 mr-2" title="Adjust"><Pencil className="w-4 h-4 inline" /></button>
                      <button data-testid={`pay-line-${i}`} onClick={() => setPayLine(l)} className="text-emerald-700 hover:underline text-xs font-medium">Pay</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pay dialog */}
      <Dialog open={!!payLine} onOpenChange={() => setPayLine(null)}>
        <DialogContent data-testid="pay-dialog">
          <DialogHeader><DialogTitle>Mark {payLine === "all" ? "all consignors" : payLine?.consignor_name} paid</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Payment date</Label><Input data-testid="pay-date" type="date" value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} /></div>
              <div><Label>Method</Label><Sel data-testid="pay-method" value={pay.payment_method} onChange={(e) => setPay({ ...pay, payment_method: e.target.value })}>
                {["Check", "Cash", "Bank Transfer", "PayPal", "Venmo", "Store Credit"].map((m) => <option key={m}>{m}</option>)}</Sel></div>
            </div>
            <div><Label>Reference / confirmation #</Label><Input data-testid="pay-ref" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></div>
            <div><Label>Notes</Label><Input data-testid="pay-notes" value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setPayLine(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm">Cancel</button>
            <button data-testid="confirm-pay-btn" onClick={doPay} className="px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm">Confirm Payment</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust dialog */}
      <Dialog open={!!adjLine} onOpenChange={() => setAdjLine(null)}>
        <DialogContent data-testid="adjust-dialog">
          <DialogHeader><DialogTitle>Manual Adjustment — {adjLine?.consignor_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Calculated payout: <b>{money(adjLine?.consignor_share_calculated, cur)}</b></p>
            <div><Label>Adjustment amount (+/-)</Label><Input data-testid="adjust-amount" type="number" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} /></div>
            <div><Label>Reason (required)</Label><Input data-testid="adjust-reason" value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} placeholder="e.g. Consignor agreed to special discount" /></div>
            <p className="text-sm text-slate-700">Final payout: <b className="text-amber-700">{money((Number(adjLine?.consignor_share_calculated || 0) + Number(adj.amount || 0)), cur)}</b></p>
          </div>
          <DialogFooter>
            <button onClick={() => setAdjLine(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm">Cancel</button>
            <button data-testid="save-adjust-btn" onClick={doAdjust} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm">Save Adjustment</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
